const NO_OP = () => {};

export class PrintCoordinator {
    #readinessById = new Map<string, boolean>();
    #listeners = new Set<() => void>();

    setReadiness(id: string, isReady: boolean) {
        if (this.#readinessById.get(id) === isReady) {
            return;
        }

        this.#readinessById.set(id, isReady);
        this.#emit();
    }

    remove(id: string) {
        if (this.#readinessById.delete(id)) {
            this.#emit();
        }
    }

    beginPreparation() {
        for (const id of this.#readinessById.keys()) {
            this.#readinessById.set(id, false);
        }

        this.#emit();
    }

    get isReady() {
        return [...this.#readinessById.values()].every(isReady => isReady);
    }

    subscribe(listener: () => void) {
        this.#listeners.add(listener);

        return () => {
            this.#listeners.delete(listener);
        };
    }

    waitUntilReady({ timeoutMs }: WaitUntilReadyOptions): Promise<void> {
        if (this.isReady) {
            return Promise.resolve();
        }

        let unsubscribe = NO_OP;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const readiness = new Promise<void>(resolve => {
            unsubscribe = this.subscribe(() => {
                if (this.isReady) {
                    resolve();
                }
            });
        });

        const expiry = new Promise<void>(resolve => {
            timeoutId = setTimeout(resolve, timeoutMs);
        });

        return Promise.race([readiness, expiry]).finally(() => {
            unsubscribe();
            clearTimeout(timeoutId);
        });
    }

    #emit() {
        for (const listener of this.#listeners) {
            listener();
        }
    }
}

export interface WaitUntilReadyOptions {
    timeoutMs: number;
}
